"use client";

import { GoogleIcon } from "@/components/google-icon";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";
import { useRouter } from "next/navigation";
import { useState } from "react";

export default function SignInPage() {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const router = useRouter();

	async function redirectAfterLogin() {
		router.push("/dashboard");
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError("");
		setLoading(true);

		const { error: authError } = await authClient.signIn.email({
			email,
			password,
		});

		if (authError) {
			setError(authError.message ?? "Sign in failed");
			setLoading(false);
			return;
		}

		await redirectAfterLogin();
	}

	return (
		<div className="flex min-h-screen flex-col items-center justify-center px-6">
			<div className="mb-8 flex items-center gap-2">
				<div className="flex h-8 w-8 items-center justify-center rounded-md bg-indigo-500">
					<span className="font-sans text-lg font-semibold text-white">u</span>
				</div>
				<span className="font-mono text-sm text-foreground/80">unranked</span>
			</div>

			<Card className="w-full max-w-sm border-border bg-card/50">
				<CardContent className="pt-6">
					<h1 className="mb-6 text-center font-sans text-2xl font-semibold text-foreground">
						Welcome back
					</h1>

					<form onSubmit={handleSubmit} className="flex flex-col gap-4">
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="email" className="font-mono text-xs text-muted-foreground">
								Email
							</Label>
							<Input
								id="email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="you@example.com"
								required
								className="h-10 font-mono text-sm"
							/>
						</div>

						<div className="flex flex-col gap-1.5">
							<Label htmlFor="password" className="font-mono text-xs text-muted-foreground">
								Password
							</Label>
							<Input
								id="password"
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								className="h-10 font-mono text-sm"
							/>
						</div>

						{error && <p className="font-mono text-xs text-red-400">{error}</p>}

						<Button
							type="submit"
							disabled={loading}
							className="mt-2 h-10 bg-indigo-500 font-mono text-sm tracking-wide text-white hover:bg-indigo-400"
						>
							{loading ? "Signing in..." : "Sign in"}
						</Button>
					</form>

					<div className="my-6 flex items-center gap-3">
						<div className="h-px flex-1 bg-border" />
						<span className="font-mono text-xs text-muted-foreground">or</span>
						<div className="h-px flex-1 bg-border" />
					</div>

					<Button
						type="button"
						variant="outline"
						onClick={() =>
							authClient.signIn.social({
								provider: "google",
								callbackURL: `${process.env.NEXT_PUBLIC_SITE_URL || "https://unranked.io"}/dashboard`,
							})
						}
						className="h-10 w-full font-mono text-sm"
					>
						<GoogleIcon className="mr-2 h-4 w-4" />
						Continue with Google
					</Button>

					<p className="mt-6 text-center font-mono text-xs text-muted-foreground">
						Don&apos;t have an account?{" "}
						<a href="/sign-up" className="text-indigo-400 hover:text-indigo-500">
							Sign up
						</a>
					</p>
				</CardContent>
			</Card>
		</div>
	);
}
